"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useLayoutEffect, useState } from "react";

/**
 * Tiny ResizeObserver hook returning a measured element's height. Used to
 * pad the transcript's scroll area so the docked composer never covers the
 * last message.
 *
 * The returned `ref` is a **callback ref**, tracked as state, so the effect
 * re-runs when the element attaches. That matters because the measured
 * element lives behind a conditional branch — the docked composer renders
 * only once the conversation has messages — while this hook is called from
 * the component above that branch. A ref object would be `null` on the one
 * pass a `[]`-dep effect ever made, leaving the height pinned at 0 and the
 * composer covering the transcript for the life of the chat.
 *
 * The rAF debounce below exists to prevent React #185 ("Maximum update depth
 * exceeded"); keep it if you touch this.
 */
export function useElementHeight() {
  const $ = _c(6);
  const [element, setElement] = useState(null);
  const [height, setHeight] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = node => {
      setElement(node);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const ref = t0;
  let t1;
  let t2;
  if ($[1] !== element) {
    t1 = () => {
      if (!element) {
        setHeight(0);
        return;
      }
      let frame = 0;
      let last = Math.round(element.offsetHeight);
      setHeight(last);
      const measure = () => {
        frame = 0;
        const next = Math.round(element.offsetHeight);
        if (next !== last) {
          last = next;
          setHeight(next);
        }
      };
      const ro = new ResizeObserver(() => {
        if (frame === 0) {
          frame = requestAnimationFrame(measure);
        }
      });
      ro.observe(element);
      return () => {
        if (frame !== 0) {
          cancelAnimationFrame(frame);
        }
        ro.disconnect();
      };
    };
    t2 = [element];
    $[1] = element;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[4] !== height) {
    t3 = [ref, height];
    $[4] = height;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}